/**
 * Mappers for reconciling UI types with Database types
 *
 * These functions transform between:
 * - CustomerCreditRow (database schema - snake_case)
 * - CustomerCredit    (UI schema - camelCase)
 */

import type {
  CustomerCredit,
  CreditType,
  CreditStatus,
  CreditReasonCategory,
  CreditAttributionType,
} from "@/data/types";
import type { CustomerCreditRow } from "@/services/credit.service";

// ── DB → UI ───────────────────────────────────────────────────────────────

export function toUiCredit(db: CustomerCreditRow): CustomerCredit {
  return {
    id: db.credit_id,
    customerId: db.customer_id,
    // Return job_number so it matches Job.id (which is job_number in the frontend)
    jobId: db.job_number ?? db.job_id,
    issueId: db.issue_id ?? undefined,
    relatedContractorId: db.related_contractor_id ?? undefined,
    relatedCrewName: db.related_crew_name ?? undefined,
    creditAmount: db.credit_amount,
    creditType: db.credit_type as CreditType,
    reasonCategory: db.reason_category as CreditReasonCategory,
    attributionType: db.attribution_type as CreditAttributionType,
    notes: db.notes ?? "",
    status: db.status as CreditStatus,
    dateCreated: db.created_at.split("T")[0],
    dateOffered: db.date_offered ?? undefined,
    createdBy: db.created_by,
    approvedBy: db.approved_by ?? undefined,
  };
}

// ── UI → DB (create) ──────────────────────────────────────────────────────
// Note: job_id (UUID) must be resolved from job_number before inserting.
// Pass the resolved UUID as the second argument.

export function toDbCreditCreate(
  ui: CustomerCredit,
  jobUuid: string
): Omit<CustomerCreditRow, "id" | "created_at" | "updated_at"> {
  return {
    credit_id: ui.id,
    customer_id: ui.customerId,
    job_id: jobUuid,
    job_number: ui.jobId,
    issue_id: ui.issueId ?? null,
    related_contractor_id: ui.relatedContractorId ?? null,
    related_crew_name: ui.relatedCrewName ?? null,
    credit_amount: ui.creditAmount,
    credit_type: ui.creditType,
    reason_category: ui.reasonCategory,
    attribution_type: ui.attributionType,
    notes: ui.notes || null,
    status: ui.status,
    date_offered: ui.dateOffered ?? null,
    approved_by: ui.approvedBy ?? null,
    created_by: ui.createdBy,
  };
}

// ── UI → DB (update) ──────────────────────────────────────────────────────

export function toDbCreditUpdate(
  ui: Partial<CustomerCredit>
): Partial<Omit<CustomerCreditRow, "id" | "created_at" | "updated_at">> {
  const patch: Partial<Omit<CustomerCreditRow, "id" | "created_at" | "updated_at">> = {};

  if (ui.creditAmount !== undefined) patch.credit_amount = ui.creditAmount;
  if (ui.creditType !== undefined) patch.credit_type = ui.creditType;
  if (ui.reasonCategory !== undefined) patch.reason_category = ui.reasonCategory;
  if (ui.attributionType !== undefined) patch.attribution_type = ui.attributionType;
  if (ui.notes !== undefined) patch.notes = ui.notes || null;
  if (ui.status !== undefined) patch.status = ui.status;
  if (ui.dateOffered !== undefined) patch.date_offered = ui.dateOffered ?? null;
  if (ui.approvedBy !== undefined) patch.approved_by = ui.approvedBy ?? null;

  return patch;
}

// ── Batch ─────────────────────────────────────────────────────────────────

export function toUiCredits(rows: CustomerCreditRow[]): CustomerCredit[] {
  return rows.map(toUiCredit);
}
